import React, { useEffect, useState } from 'react';
import type { Member } from './EditMemberForm';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';

interface Sport {
  id: number;
  name: string;
}

interface SportMembersProps {
  apiBaseUrl: string;
  accessToken: string | null;
  userRoles?: string[];
  userSports?: string[];
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

const SportMembers: React.FC<SportMembersProps> = ({ apiBaseUrl, accessToken, userRoles, userSports }) => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${apiBaseUrl}/sports`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setSports(data);
      });
  }, [apiBaseUrl, accessToken]);

  useEffect(() => {
    if (selectedSportId === null) return;
    setLoading(true);
    setError(null);
    fetch(`${apiBaseUrl}/sports/${selectedSportId}/members`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setMembers(data);
        else setError('Failed to fetch members for this sport.');
      })
      .catch(() => setError('Failed to fetch members for this sport.'))
      .finally(() => setLoading(false));
  }, [selectedSportId, apiBaseUrl, accessToken]);

  // Table columns for sport members
  const columnHelper = createColumnHelper<Member>();
  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue(),
      enableSorting: true,
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: info => info.getValue(),
      enableSorting: true,
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => info.getValue(),
      enableSorting: true,
    }),
    columnHelper.accessor('membership_type', {
      header: 'Membership Type',
      cell: info => info.getValue() || '',
      enableSorting: true,
    }),
    columnHelper.accessor('membership_status', {
      header: 'Membership Status',
      cell: info => info.getValue() || '',
      enableSorting: true,
    }),
  ];

  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data: members,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Filter sports for dropdown: admins/trustees see all, sports admins see only their sports
  const sportsForUser = hasRole(userRoles, 'admin') || hasRole(userRoles, 'trustee')
    ? sports
    : sports.filter(s => (userSports || getUserSports(userRoles)).includes(s.name.toLowerCase()));

  return (
    <div className="py-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Members by Sport</h2>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <select
          className="w-full sm:w-72 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={selectedSportId ?? ''}
          onChange={e => setSelectedSportId(Number(e.target.value) || null)}
        >
          <option value="">Select a sport...</option>
          {sportsForUser.map(sport => (
            <option key={sport.id} value={sport.id}>{sport.name}</option>
          ))}
        </select>
      </div>
      {loading && <div className="text-center py-8">Loading members...</div>}
      {error && <div className="text-center py-8 text-red-600">Error: {error}</div>}
      {!loading && !error && selectedSportId && (
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-200">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b cursor-pointer select-none"
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() ? (
                        <span className="ml-1">{header.column.getIsSorted() === 'asc' ? '▲' : '▼'}</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="py-3 px-4 border-b border-gray-200 text-sm text-gray-800">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SportMembers;
